import React from 'react';
import clsx from 'clsx';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import { getFlavorDisplayName } from 'utils/flavorDisplay';
import { formatDateWithMoment } from 'utils/helpers';
import Permission from 'components/ValidPermission';
import Checkbox from '@material-ui/core/Checkbox';
import i18n from 'i18n';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import {
  duplicateModelRequest,
  editModelRequest,
  removeModelRequest,
} from 'modules/environments/actions';
import { modalClose, modalOpen } from 'modules/modal/actions';
import {
  CloudProviderEnum,
  EnvironmentFields,
  ListModelFieldsDataProps,
  ModelFields,
} from 'modules/environments/interfaces';
import { Grid, Tooltip, makeStyles, Typography } from '@material-ui/core';
import { ModalFields } from 'modules/modal/interfaces';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import Button from 'components/Button';
import FormDuplicate from '../FormDuplicate';
import FormModel from '../FormModel';
import { getLicenseTypeDisplayValue } from '../FormModel/licenseTypeDisplay';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Query {
  offset: number;
  limit: number;
  search: string;
}

interface ModelsTableProps {
  ambUuid: string;
  checks: string[];
  environmentSelected: EnvironmentFields | null;
  headers: Array<TableCellHeaderProps>;
  isLoading: boolean;
  modelsList: ListModelFieldsDataProps;
  query: Query;
  refresh: boolean;
  duplicateModelRequest: ({ id, modelUuid, values, query }) => void;
  editModelRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  removeModelRequest: (props) => void;
  setChecks: (checks: string[]) => void;
  setQuery: React.Dispatch<React.SetStateAction<Query>>;
}

const ModelsTable: React.FC<ModelsTableProps> = ({
  ambUuid,
  checks,
  environmentSelected,
  headers,
  isLoading,
  modelsList,
  query,
  refresh,
  duplicateModelRequest,
  editModelRequest,
  modalOpen,
  modalClose,
  removeModelRequest,
  setChecks,
  setQuery,
}) => {
  const classes = useStyles();

  const [itemOrder, setItemOrder] = React.useState('');
  const [order, setOrder] = React.useState(false);
  const [selectedAll, setSelectedAll] = React.useState(false);

  const isOci = environmentSelected?.provider?.type === CloudProviderEnum.OCI;

  const handleRemoveModel = (model: ModelFields) => {
    modalOpen({
      title: i18n.t('WANT_DELETE_MODEL_KEY'),
      content: i18n.t('WANT_DELETE_MODEL_DESCRIPTION_KEY', {
        name: model?.name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              removeModelRequest({
                id: ambUuid,
                model,
                url: model?.model_details_path + model?.model_uuid,
              });
              modalClose();
            }}
          >
            {i18n.t('DELETE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handlePageChange = (page: number) => {
    setQuery(prev => ({
      ...prev,
      offset: page * query.limit,
    }));
  };

  const handleLimitChange = (limit: number) => {
    setQuery(prev => ({
      ...prev,
      limit,
    }));
  };

  const handleAllCheck = () => {
    if (!selectedAll) {
      const ids: string[] = [];
      modelsList.results.forEach(item => ids.push(item?.model_uuid));
      setChecks(ids);
    } else {
      setChecks([]);
    }
    setSelectedAll(!selectedAll);
  };

  const handleCheck = (uuid: string) => {
    const findCheckId = checks.find(item => item === uuid);
    if (!findCheckId) {
      setChecks([...checks, uuid]);
    } else {
      const index = checks.indexOf(findCheckId);
      checks.splice(index, 1);
      setChecks([...checks]);
    }
  };

  const handleOrder = (keyOrder: string) => {
    setItemOrder(keyOrder);
    setQuery(prev => ({
      ...prev,
      ordering: `${order ? '-' : ''}${keyOrder}`,
    }));
    setOrder(!order);
  };

  const handleOpenEditModal = (item: ModelFields) => () =>
    modalOpen({
      maxWidth: 'md',
      title: i18n.t('EDIT_MODEL_KEY'),
      content: (
        <FormModel
          onSubmit={({ values, pathUrl }) => {
            editModelRequest({ id: ambUuid, values, pathUrl });
          }}
          onCancel={modalClose}
          getDetailsUrl={item?.model_details_path + item?.model_uuid}
          cloud={environmentSelected?.provider?.type}
          modelFields={item}
        />
      ),
    });

  const handleOpenDuplicateModal = (item: ModelFields) => () =>
    modalOpen({
      title: i18n.t('WANT_DUPLICATE_MODEL_KEY'),
      noBorder: true,
      content: (
        <FormDuplicate
          modelName={item?.name}
          onSubmit={values => {
            duplicateModelRequest({
              id: ambUuid,
              modelUuid: item?.model_uuid,
              values,
              query,
            });
            modalClose();
          }}
          onCancel={modalClose}
        />
      ),
    });

  const getModelRoleLabel = (modelRole: string) => {
    const mappedCases = {
      backup: i18n.t('BACKUP_SECONDARY_KEY'),
      main: i18n.t('MODEL_PRIMARY_KEY'),
      spot: 'Spot',
    };
    const modelRoleLabel = mappedCases[modelRole];

    if (!modelRole) return modelRole;
    return modelRoleLabel;
  };

  return (
    <TableItems
      header={
        <>
          <TableCell className={classes.checkboxCell}>
            <Checkbox onChange={handleAllCheck} />
          </TableCell>
          {headers.map(item => (
            <TableCell
              centerTitle={item?.centerTitle}
              key={item?.key}
              order={item?.order}
              markOderItem={itemOrder === item?.key}
              handleOrder={() => (item?.order ? handleOrder(item?.key) : null)}
            >
              {`${i18n.t(item?.i18n)}`}
            </TableCell>
          ))}
          <TableCell centerTitle>{i18n.t('ACTIONS_KEY')}</TableCell>
        </>
      }
      renderItems={(item: ModelFields, index) => {
        return (
          <TableRow key={item?.model_uuid}>
            <TableCell className={classes.checkboxCell}>
              <Checkbox
                onChange={() => handleCheck(item?.model_uuid)}
                checked={!!checks.find(check => check === item?.model_uuid)}
              />
            </TableCell>
            <TableCell>
              {item?.name}
              {!!item?.model_role && (
                <span
                  className={clsx(classes.flag, {
                    [classes.flagRed]: item?.model_role === 'spot',
                    [classes.flagNavOperator]: item?.model_role === 'backup',
                  })}
                >
                  {getModelRoleLabel(item?.model_role)}
                </span>
              )}
            </TableCell>
            <TableCell align="center">
              <span className={classes.version}>{item?.version}</span>
            </TableCell>
            <TableCell centerTitle>
              {formatDateWithMoment(item?.creation_date)}
            </TableCell>
            <TableCell centerTitle>
              {i18n.t(`${item?.model_type.toUpperCase()}_KEY`)}
            </TableCell>
            <TableCell>
              {getFlavorDisplayName(item?.provider_data?.flavor)}
            </TableCell>
            {isOci && (
              <TableCell centerTitle>
                {getLicenseTypeDisplayValue(item?.provider_data?.license_type)}
              </TableCell>
            )}
            <TableCell align="center">{item?.active_instances_count}</TableCell>
            <TableCell align="center">
              {item?.active_instances_sessions_count}
            </TableCell>
            <TableCell textAlignCenter>
              <MenuActions id={`btn-options-models-${index}`}>
                <Permission value="edit_models">
                  <MenuActionItem
                    id="btn-edit-model"
                    onClick={handleOpenEditModal(item)}
                  >
                    {i18n.t('EDIT_KEY')}
                  </MenuActionItem>
                </Permission>
                <MenuActionItem
                  id="btn-duplicate-model"
                  onClick={handleOpenDuplicateModal(item)}
                >
                  {i18n.t('DUPLICATE_KEY')}
                </MenuActionItem>
                <Permission value="delete_models">
                  <MenuActionItem
                    id="btn-delete-model"
                    onClick={() =>
                      item?.model_role !== 'main' && handleRemoveModel(item)
                    }
                  >
                    {item?.model_role === 'main' ? (
                      <Tooltip
                        placement="bottom"
                        arrow
                        title={i18n.t('CANT_DELETE_MODEL_KEY')}
                      >
                        <Typography className={classes.deleteTextDisable}>
                          {i18n.t('DELETE_KEY')}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography className={classes.deleteText}>
                        {i18n.t('DELETE_KEY')}
                      </Typography>
                    )}
                  </MenuActionItem>
                </Permission>
              </MenuActions>
            </TableCell>
          </TableRow>
        );
      }}
      data={modelsList}
      isLoading={!refresh && isLoading}
      onChangePage={handlePageChange}
      onChangeLimit={handleLimitChange}
      notFoundText={i18n.t('ENVIRONMENTS_PAGE.NO_MODEL_KEY')}
      colCount={isOci ? 12 : 11}
    />
  );
};

const mapStateToProps = ({
  environments: { isLoading, modelsList, environmentSelected },
}: ApplicationState) => ({
  isLoading,
  modelsList,
  environmentSelected,
});

export default connect(mapStateToProps, {
  removeModelRequest,
  editModelRequest,
  duplicateModelRequest,
  modalClose,
  modalOpen,
})(ModelsTable);
